import React, { useState } from "react";
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Spinner,
  Divider,
  Card,
  CardBody,
  Badge,
} from "@chakra-ui/react";
import { useSnapTradeController } from "./useSnapTradeController";
import SnapTradeHeader from "./SnapTradeHeader";
import SnapTradeBenefits from "./SnapTradeBenefits";
import SnapTradeHowItWorks from "./SnapTradeHowItWorks";
import BrokerSelector from "./BrokerSelector";
import ConnectionsList from "./ConnectionsList";
import ConnectionDetailModal from "./ConnectionDetailModal";
import { SnapTradeReact } from "snaptrade-react";
import { AutoSync } from "../../../AutoSync/AutoSync";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

interface SnapTradeUploadProps {
  closeModal: () => void;
  goBackButton?: React.ReactNode;
}

const SnapTradeUpload: React.FC<SnapTradeUploadProps> = ({ closeModal, goBackButton }) => {
  const controller = useSnapTradeController({ closeModal });
  const isNativeBroker = controller.broker === "SCHWAB" || controller.broker === "INTERACTIVE-BROKERS-FLEX" || controller.broker === "INTERACTIVEBROKERS";

  const [showInfoDialog, setShowInfoDialog] = useState(false);

  return (
    <Box minH="400px" maxW="800px" mx="auto">
      {!goBackButton ? null : <Box pb="16px">{goBackButton}</Box>}

      <VStack alignItems="flex-start" spacing={6} w="full">
        {/* Header Section */}
        <Box w="full">
          <SnapTradeHeader />
          <Text color="var(--semi-white)" mt={3} lineHeight="1.6">
            Securely link your brokerage accounts to automatically sync positions and transactions into your portfolio.
          </Text>
        </Box>
        {/* Information Cards */}
        <Box w="full">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowInfoDialog(!showInfoDialog)}
            color="var(--semi-white)"
            _hover={{ bg: "var(--dark-gray)" }}
            leftIcon={showInfoDialog ? <ChevronUpIcon /> : <ChevronDownIcon />}
          >
            {showInfoDialog ? "Hide" : "Show"} benefits and how it works
          </Button>
          
          {showInfoDialog && (
            <VStack w="full" spacing={4} mt={4}>
              <SnapTradeBenefits />
              <SnapTradeHowItWorks />
            </VStack>
          )}
        </Box>


        {/* Connection Setup Section */}
        <Card w="full" variant="outline" bg="transparent">
          <CardBody>
            <VStack alignItems="flex-start" spacing={4} w="full">
              <Text fontSize="lg" fontWeight={600} color="var(--semi-white)">
                Set up new connection
              </Text>
              
              <BrokerSelector broker={controller.broker} setBroker={controller.setBroker} />

              <Box>
                <Text fontSize="sm" color="var(--light-gray)" mb={1}>Selected portfolio:</Text>
                <Badge 
                  colorScheme={controller.selectedPortfolioName ? "green" : "gray"} 
                  variant="subtle"
                  fontSize="sm"
                  px={3}
                  py={1}
                >
                  {controller.selectedPortfolioName || "None selected"}
                </Badge>
              </Box>

              {(controller.errorMessage || controller.successMessage) && (
                <Box w="full">
                  {controller.errorMessage && (
                    <Text color="var(--red)" fontWeight={600} fontSize="sm">
                      {controller.errorMessage}
                    </Text>
                  )}
                  {controller.successMessage && (
                    <Text color="var(--green)" fontWeight={600} fontSize="sm">
                      {controller.successMessage}
                    </Text>
                  )}
                </Box>
              )}

              {!isNativeBroker && (
                <HStack spacing={3}>
                  <Button
                    onClick={controller.connect}
                    isDisabled={
                      controller.isLoading || !controller.hasRequiredSelections
                    }
                    colorScheme="blue"
                    size="md"
                    title={
                      !controller.broker
                        ? "Select a broker"
                        : !controller.selectedPortfolioId
                        ? "Select a portfolio"
                        : undefined
                    }
                  >
                    {controller.isLoading ? <Spinner size="sm" mr={2} /> : null}
                    Connect
                  </Button>
                 
                </HStack>
              )}

              {isNativeBroker && (
                <Box w="full" mt={2}>
                  <Text fontSize="sm" color="gray.400" mb={3}>
                    {(controller.broker === "SCHWAB" ? "Schwab" : "Interactive Brokers (IBKR)") + " supports native Auto Sync. Configure it below."}
                  </Text>
                  <AutoSync />
                </Box>
              )}

              <Text fontSize="xs" color="var(--light-gray)" fontStyle="italic">
                Read-only access; no trading permissions are granted.
              </Text>
            </VStack>
          </CardBody>
        </Card>

        {!isNativeBroker && (
          <SnapTradeReact
            isOpen={controller.connectModalOpen}
            loginLink={controller.loginLink}
            close={controller.handleConnectClose}
            onSuccess={controller.handleConnectSuccess}
            onError={controller.handleConnectError}
            style={{
              overlay: {
                zIndex: 3000,
                backgroundColor: "rgba(0, 0, 0, 0.75)",
              },
            }}
          />
        )}

        {/* Sync progress */}
        {controller.isSyncLoading && (
          <HStack w="full" justifyContent="flex-start" color="var(--light-gray)">
            <Spinner size="sm" />
            <Text fontSize="sm">Syncing your latest transactions...</Text>
          </HStack>
        )}

        {/* Existing Connections Section */}
        <ConnectionsList
          connections={controller.connections}
          onView={controller.viewConnection}
          onDelete={controller.deleteConnection}
        />

        <ConnectionDetailModal
          isOpen={controller.detailOpen}
          onClose={controller.closeDetail}
          connection={controller.selectedConnection}
        />
      </VStack>
    </Box>
  );
};

export default SnapTradeUpload;


